import { useState } from 'react';
import { RefreshCw, Send, XCircle } from 'lucide-react';
import { problemText, useApiProblem, type UpgradeChargeResponse } from '@/shared/api';
import { Button, ConfirmDialog, Pill } from '@/shared/components';
import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { SectionCard } from '../detail-view/SectionCard';
import { StaleNote } from '../detail-view/StaleNote';
import {
  canCancelUpgradeCharge,
  canSendUpgradeCharge,
  upgradeChargePresentation,
} from './upgradeChargePresentation';
import { useUpgradeCharges } from './useUpgradeCharges';

export function UpgradeChargesCard({ bookingId }: { bookingId: string }) {
  const state = useUpgradeCharges(bookingId);
  const charges = state.query.data ?? [];
  const problem = useApiProblem(state.query.error);
  const failed = state.query.isError && !state.query.data;
  // Aufheben beendet den Zahlungsweg des Gastes – erst nach Rückfrage.
  const [confirming, setConfirming] = useState<UpgradeChargeResponse | null>(null);
  return (
    <SectionCard
      icon="Receipt"
      title="Aufpreise"
      trailing={state.query.data ? <Pill variant="numeric">{charges.length}</Pill> : undefined}
    >
      {state.query.isLoading && <p className="text-ink-soft text-sm">Aufpreise werden geladen…</p>}
      {failed && (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm">
          <span className="min-w-0 flex-1">
            {problemText(problem, 'Die Aufpreise konnten nicht geladen werden.')}
          </span>
          <Retry onClick={() => void state.query.refetch()} label="Erneut laden" />
        </div>
      )}
      {state.query.isError && state.query.data && (
        <StaleNote onRetry={() => void state.query.refetch()} />
      )}
      {state.query.data && charges.length === 0 && (
        <p className="text-ink-soft text-sm">
          Noch kein separater Aufpreis. Neue Forderungen erscheinen unabhängig vom Buchungspreis.
        </p>
      )}
      <div className="space-y-3">
        {charges.map((charge) => {
          const presentation = upgradeChargePresentation[charge.status];
          return (
            <article
              key={charge.upgradeChargeId}
              className="border-hairline bg-paper rounded-control border p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-ink font-semibold">{charge.description}</p>
                  <p className="text-ink-faint mt-1 text-xs">
                    Angelegt {formatDateTime(charge.createdAt)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-ink font-mono font-semibold tabular-nums">
                    {formatMoney(charge.grossAmount, charge.currency)}
                  </p>
                  <Pill className="mt-1" variant={presentation.variant}>
                    {presentation.label}
                  </Pill>
                </div>
              </div>
              <p className="text-ink-soft mt-3 text-sm">{presentation.detail}</p>
              {(canSendUpgradeCharge(charge.status) || canCancelUpgradeCharge(charge.status)) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {canSendUpgradeCharge(charge.status) && (
                    <Button
                      size="sm"
                      variant="primary"
                      leftIcon={<Send size={14} />}
                      loading={
                        state.send.isPending && state.send.variables === charge.upgradeChargeId
                      }
                      onClick={() => state.send.mutate(charge.upgradeChargeId)}
                    >
                      {charge.status === 'Draft' ? 'Link senden' : 'Link erneut senden'}
                    </Button>
                  )}
                  {canCancelUpgradeCharge(charge.status) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<XCircle size={14} />}
                      loading={
                        state.cancel.isPending && state.cancel.variables === charge.upgradeChargeId
                      }
                      onClick={() => setConfirming(charge)}
                    >
                      Aufheben
                    </Button>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && setConfirming(null)}
        title="Aufpreis aufheben?"
        description={
          confirming
            ? `„${confirming.description}“ (${formatMoney(confirming.grossAmount, confirming.currency)}) kann der Gast danach nicht mehr bezahlen. Läuft schon eine Zahlung, wird sie storniert oder voll erstattet.`
            : ''
        }
        confirmLabel="Aufheben"
        cancelLabel="Behalten"
        onConfirm={() => {
          if (confirming) state.cancel.mutate(confirming.upgradeChargeId);
          setConfirming(null);
        }}
      />
    </SectionCard>
  );
}

function Retry({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button
      variant="ghost"
      className="min-h-11"
      leftIcon={<RefreshCw size={14} />}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
